<template>
    <div>
        <Tabs>
            <Tab :title="$t('messages.select_random_student')">
                <div class="pt-2 md:pt-6">
                    <div class="max-w-7xl mx-auto md:px-4 sm:px-6 md:px-8">
                        <div class="w-full flex justify-end flex-wrap">
                            <transition
                                enter-active-class="transition ease-out duration-100"
                                enter-from-class="transition ease-out duration-100"
                                enter-to-class="transform opacity-100 scale-100"
                                leave-active-class="transition ease-in duration-75"
                                leave-from-class="transform opacity-100 scale-100"
                                leave-to-class="transform opacity-0 scale-95"
                            >
                                <div v-show="selectedStudents.length > 0" class="flex">
                                    <span class="relative z-0 inline-flex shadow-sm rounded-md">
                                        <button
                                            type="button"
                                            @click="getRandom()"
                                            :disabled="sameSet"
                                            class="relative inline-flex items-center px-4 rounded-l-md border border-gray-300 bg-white text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:ring-blue active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150"
                                        >
                                            <svg
                                                class="w-4 h-4 text-gray-400"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M11 17a1 1 0 001.447.894l4-2A1 1 0 0017 15V9.236a1 1 0 00-1.447-.894l-4 2a1 1 0 00-.553.894V17zM15.211 6.276a1 1 0 000-1.788l-4.764-2.382a1 1 0 00-.894 0L4.789 4.488a1 1 0 000 1.788l4.764 2.382a1 1 0 00.894 0l4.764-2.382zM4.447 8.342A1 1 0 003 9.236V15a1 1 0 00.553.894l4 2A1 1 0 009 17v-5.764a1 1 0 00-.553-.894l-4-2z"
                                                ></path>
                                            </svg>
                                            {{ $t('messages.select_random_student') }}
                                        </button>
                                        <button
                                            type="button"
                                            @click="clearRandomSelected()"
                                            class="-ml-px relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:ring-blue active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150"
                                        >
                                            <svg
                                                class="w-4 h-4 text-gray-400"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    fill-rule="evenodd"
                                                    d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                                                    clip-rule="evenodd"
                                                ></path>
                                            </svg>
                                        </button>
                                    </span>
                                </div>
                            </transition>
                        </div>

                        <div v-show="randomSelected.length > 0" class="pt-3">
                            <div class="bg-blue-200 p-4 md:grid md:grid-cols-3 md:gap-6">
                                <div
                                    v-for="student in selectedRandomStudents"
                                    class="relative col-span-1 flex shadow rounded-md"
                                >
                                    <div
                                        class="shrink-0 flex items-center justify-center w-16 text-white text-sm leading-5 font-medium rounded-l-md overflow-hidden"
                                    >
                                        <img :src="student.avatar_or_default" alt="profile photo" />
                                    </div>
                                    <div
                                        class="flex-1 flex items-center justify-between border-t border-r border-b border-gray-200 bg-white rounded-r-md truncate"
                                    >
                                        <div class="flex-1 px-4 py-2 text-sm leading-5 truncate">
                                            <div
                                                href="#"
                                                class="text-gray-900 font-medium hover:text-gray-600 transition ease-in-out duration-150"
                                            >
                                                {{ student.full_name }}
                                                <span v-if="isLatestSelected(student.id)" class="inline-block">
                                                    <svg
                                                        class="w-4 h-4 text-indigo-600"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            stroke-linecap="round"
                                                            stroke-linejoin="round"
                                                            stroke-width="2"
                                                            d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                                                        ></path>
                                                    </svg>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Tab>

            <Tab :title="$t('messages.list_of_selected_students')">
                <div class="mt-1 flex rounded-md shadow-sm">
                    <TitleFieldsFormSection>
                        <template v-slot:title>
                            {{ $t('messages.pdf') }}
                        </template>
                        <form :action="listFormRoute" method="get" target="_blank">
                            <div class="md:grid md:grid-cols-12 md:gap-6">
                                <div class="sm:col-span-6">
                                    <div class="mt-1">
                                        <FormLabel :value="$t('messages.number_of_columns')" for="grade_level_id" />
                                        <FormInput
                                            type="number"
                                            min="1"
                                            max="40"
                                            step="1"
                                            value="1"
                                            name="columns"
                                            class="mt-1"
                                        />
                                    </div>
                                </div>

                                <div class="sm:col-span-6">
                                    <div class="mt-1">
                                        <label>
                                            {{ $tc('messages.teams', 2) }}
                                            <input
                                                type="checkbox"
                                                id="teams"
                                                name="teams"
                                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                            />
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <input
                                    v-for="student in selectedStudents"
                                    type="hidden"
                                    name="student[]"
                                    :value="student.id"
                                    :key="student.id"
                                />

                                <AppButton desired-action element="button" type="submit" class="mt-3">
                                    <svg
                                        class="w-4 h-4 mr-1"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            fill-rule="evenodd"
                                            d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z"
                                            clip-rule="evenodd"
                                        ></path>
                                    </svg>
                                    {{ $t('messages.pdf') }}
                                </AppButton>
                            </div>
                        </form>
                    </TitleFieldsFormSection>
                </div>
            </Tab>
        </Tabs>

        <div class="pt-2 pb-6 md:py-6">
            <div class="max-w-7xl mx-auto md:px-4 sm:px-6 md:px-8">
                <div class="md:grid md:grid-cols-3 md:gap-6">
                    <div class="mt-5 md:mt-0 md:col-span-3">
                        <div class="shadow sm:rounded-md sm:overflow-hidden">
                            <div class="px-4 py-5 bg-white sm:p-6">
                                <div class="mt-3">
                                    <AppButton type="button" element="button" @click="selectAll()">
                                        {{ $t('messages.select_all') }}
                                    </AppButton>
                                    <AppButton type="button" element="button" @click="deselectAll()">
                                        {{ $t('messages.deselect_all') }}
                                    </AppButton>
                                    <AppButton type="button" element="button" @click="selectPresent()">
                                        {{ $t('messages.select_present_students') }}
                                    </AppButton>
                                    <div class="inline-block">
                                        ({{ selectedStudents.length }} / {{ students.length }})
                                    </div>
                                </div>

                                <transition-group
                                    tag="div"
                                    name="card"
                                    class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4"
                                >
                                    <div
                                        v-for="student in finalSortedSelected"
                                        :key="`${student.id}_${student.selected}`"
                                        @click="student.selected = !student.selected"
                                        class="relative col-span-1 flex shadow rounded-md cursor-pointer"
                                        :class="{
                                            'opacity-50': !student.selected,
                                        }"
                                    >
                                        <div
                                            class="shrink-0 flex items-center justify-center w-16 text-white text-sm leading-5 font-medium rounded-l-md overflow-hidden"
                                        >
                                            <img :src="student.avatar_or_default" alt="profile photo" />
                                        </div>
                                        <div
                                            class="flex-1 flex items-center justify-between border-t border-r border-b border-gray-200 bg-white rounded-r-md truncate"
                                        >
                                            <div class="flex-1 px-4 py-2 text-sm leading-5 truncate">
                                                <div
                                                    class="text-gray-900 font-medium hover:text-gray-600 transition ease-in-out duration-150"
                                                >
                                                    {{ student.full_name }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </transition-group>
                            </div>
                        </div>
                    </div>

                    <div class="md:col-span-1" v-if="false">
                        <div class="bg-white overflow-hidden sm:rounded-lg sm:shadow">
                            <div class="p-4">
                                <div class="block" v-for="student in finalStudents" :key="student.id">
                                    <label>
                                        <input type="checkbox" v-model="student.selected" />
                                        {{ student.full_name }}
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import Tab from '@/components/Tab.vue';
import Tabs from '@/components/Tabs.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import ButtonPrimary from '@/components/ButtonPrimary.vue';
import AppButton from '@/components/AppButton.vue';

export default {
    components: {
        AppButton,
        Tab,
        Tabs,
        TitleFieldsFormSection,
        FormInput,
        FormLabel,
        ButtonPrimary,
    },
    data() {
        return {
            selected: [],
            randomSelected: [],
            finalStudents: [],
        };
    },
    computed: {
        finalSortedSelected: function () {
            return [...this.finalStudents].sort((a, b) => {
                if (a.selected === b.selected) {
                    return a.full_name.localeCompare(b.full_name);
                }

                return b.selected - a.selected;
            });
        },
        latestSelected: function () {
            return this.randomSelected.length ? this.randomSelected.slice(-1)[0] : null;
        },
        allSelected: function () {
            return this.selectedStudents.length === this.students.length;
        },
        noneSelected: function () {
            return this.selectedStudents.length === 0;
        },
        selectedStudents: function () {
            return this.finalStudents.filter((s) => s.selected);
        },
        selectedStudentsIds: function () {
            return this.selectedStudents.map((s) => s.id);
        },
        selectedRandomStudents: function () {
            return this.students.filter((s) => this.randomSelected.includes(s.id));
        },
        sameSet: function () {
            if (this.randomSelected.length !== this.selectedStudents.length) {
                return false;
            }

            if (this.selectedStudents.length === 0) {
                return false;
            }

            for (let i = 0; i < this.selectedStudentsIds.length; i++) {
                if (!this.randomSelected.includes(this.selectedStudentsIds[i])) {
                    return false;
                }
            }

            return true;
        },
    },
    methods: {
        deselectAll() {
            return this.finalStudents.forEach((s) => (s.selected = false));
        },
        isLatestSelected(id) {
            return this.latestSelected === id;
        },
        selectPresent() {
            return this.finalStudents.forEach((s) => (s.selected = !this.absent.includes(s.id)));
        },
        selectAll() {
            return this.finalStudents.forEach((s) => (s.selected = true));
        },
        getRandom() {
            const selectedNotInRandom = this.selectedStudentsIds.filter((x) => !this.randomSelected.includes(x));
            const item = selectedNotInRandom[Math.floor(Math.random() * selectedNotInRandom.length)];
            if (!this.randomSelected.includes(item)) {
                this.randomSelected.push(item);
            }
        },
        clearRandomSelected() {
            this.randomSelected = [];
        },
        add(id) {
            this.selected.push(id);
        },
        remove(id) {
            const index = this.selected.indexOf(id);
            this.selected.splice(index, 1);
        },
    },
    props: {
        students: {
            type: Array,
            required: true,
        },
        absent: {
            type: Array,
            required: true,
        },
        listFormRoute: {
            type: String,
            required: true,
        },
    },
    created() {
        this.finalStudents = this.students.map((s) => {
            s.selected = !this.absent.includes(s.id);
            return s;
        });
    },
};
</script>
<style>
.card-move {
    transition: transform 0.5s;
}
</style>
